import React, { useState } from 'react'

import classes from './TemplateServiceChartRow.module.css'

import CloseIcon from '@material-ui/icons/Close'
import TimerOffIcon from '@material-ui/icons/TimerOff'
import EventIcon from '@material-ui/icons/Event'
import { Theme, Tooltip, Popover } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { MS_PER_DAY, mix_hexes } from '../../../../helpers'
import {
    TemplateService,
    TemplateServiceVendor,
    TemplateWorkorder,
} from '../../../../models'
import { Container } from '../../../Container'
import { DAY_CELL_W, MARGIN, VENDOR_BAR_H } from '../../constants'
import { CalendarGridController } from '../../hooks'
import { DragIcon } from '../DragIcon'
import { DateTime } from 'luxon'
import { TimezoneState } from '../../../../contexts/timezone/timezone'
import { ezScheduleDetails } from '../../../../models/WorkOrder/types'
import { UpdateTemplateServiceVendorParams } from '../../../../hooks/useEzSchedule/types'

interface Props {
    projectStartDate: DateTime
    projectEndDate: DateTime
    templateServiceVendor: TemplateServiceVendor
    templateService: TemplateService
    gridState: CalendarGridController
    margin: number
    color: string
    timezoneState: TimezoneState
    minStartDate: DateTime | null
    theme: Theme
    ezSchedule: ezScheduleDetails
    templateWorkorders?: TemplateWorkorder[]
    workordersByVendorDate: Map<string, TemplateWorkorder[]>
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    selectedPhaseId: number
}

export const TemplateVendorChartBar = (props: Props) => {
    const {
        ezSchedule,
        templateService,
        gridState,
        templateServiceVendor,
        projectStartDate,
        projectEndDate,
        timezoneState,
        minStartDate,
        updateTemplateServiceVendor,
        selectedPhaseId,
    } = props

    const vendorStartDate = DateTime.fromISO(
        templateServiceVendor.start_date,
    ).setZone(timezoneState.currentTimezone)
    const vendorEndDate = DateTime.fromISO(
        templateServiceVendor.end_date,
    ).setZone(timezoneState.currentTimezone)

    const vendorStartMs = vendorStartDate.toMillis()
    const vendorEndMs = vendorEndDate.toMillis()
    const projectStartMs = projectStartDate.startOf('day').toMillis()
    const projectEndMs = projectEndDate.endOf('day').toMillis()

    const clampToRange = (dateMs: number): number => {
        return Math.min(Math.max(dateMs, projectStartMs), projectEndMs)
    }

    const updateStartDate = (nextDateMs: number): void => {
        const nextMs = Math.min(clampToRange(nextDateMs), vendorEndMs)
        updateTemplateServiceVendor({
            templateServiceVendorId: templateServiceVendor.id,
            start_date:
                DateTime.fromMillis(nextMs)
                    .setZone(timezoneState.currentTimezone)
                    .toISO() ?? undefined,
        })
    }

    const updateEndDate = (nextDateMs: number): void => {
        const nextMs = Math.max(clampToRange(nextDateMs), vendorStartMs)
        updateTemplateServiceVendor({
            templateServiceVendorId: templateServiceVendor.id,
            end_date:
                DateTime.fromMillis(nextMs)
                    .setZone(timezoneState.currentTimezone)
                    .toISO() ?? undefined,
        })
    }

    const handleStartLeftClick = (): void => {
        updateStartDate(vendorStartMs - MS_PER_DAY)
    }

    const handleStartRightClick = (): void => {
        updateStartDate(vendorStartMs + MS_PER_DAY)
    }

    const handleEndLeftClick = (): void => {
        updateEndDate(vendorEndMs - MS_PER_DAY)
    }

    const handleEndRightClick = (): void => {
        updateEndDate(vendorEndMs + MS_PER_DAY)
    }

    const [
        anchorStartCalendar,
        setAnchorStartCalendar,
    ] = useState<HTMLElement | null>(null)
    const [
        anchorEndCalendar,
        setAnchorEndCalendar,
    ] = useState<HTMLElement | null>(null)

    const handleStartDatePickerChange = (date: Date | null): void => {
        if (!date) return
        const nextMs = DateTime.fromJSDate(date)
            .setZone(timezoneState.currentTimezone)
            .startOf('day')
            .toMillis()
        updateStartDate(nextMs)
        setAnchorStartCalendar(null)
    }

    const handleEndDatePickerChange = (date: Date | null): void => {
        if (!date) return
        const nextMs = DateTime.fromJSDate(date)
            .setZone(timezoneState.currentTimezone)
            .startOf('day')
            .toMillis()
        updateEndDate(nextMs)
        setAnchorEndCalendar(null)
    }

    const iconSize = VENDOR_BAR_H / 2
    const arrowGapPx = 6
    const halfArrowGapPx = arrowGapPx / 2

    const startGridPos = gridState.getDateGridPosition(vendorStartDate)
    const endGridPos = gridState.getDateGridPosition(vendorEndDate)

    const displayEndMs =
        projectEndDate.toMillis() +
        MS_PER_DAY * gridState.gridPositionInfo.numStartPaddingDays

    const displayEndDate = DateTime.fromMillis(displayEndMs).setZone(
        timezoneState.currentTimezone,
    )

    const lastGridPos = gridState.getDateGridPosition(displayEndDate)

    const dayOffMap: {
        [dateMs: number]: 'vendor' | 'global' | 'restricted'
    } = {}

    // Add dates before minStartDate as restricted
    if (minStartDate) {
        const minStartMs = minStartDate.toMillis()
        const projectStartMs = gridState.convertGridPositionToDate(0).toMillis()

        // Add all dates from project start to minStartDate as restricted
        for (
            let dateMs = projectStartMs;
            dateMs < minStartMs;
            dateMs += MS_PER_DAY
        ) {
            dayOffMap[dateMs] = 'restricted'
        }
    }

    ezSchedule?.phases
        .find((phase) => phase.phase.id === selectedPhaseId)
        ?.day_off_list.forEach((dayOff) => {
            const dayOffDate = DateTime.fromISO(dayOff.date).setZone(
                timezoneState.currentTimezone,
            )
            const dayOffMs = dayOffDate.toMillis()
            dayOffMap[dayOffMs] = 'global'
        })

    templateServiceVendor.day_off_list.forEach((dayOff) => {
        if (dayOff.template_service_vendor === templateServiceVendor.id) {
            const dayOffDate = DateTime.fromISO(dayOff.date).setZone(
                timezoneState.currentTimezone,
            )
            const dayOffMs = dayOffDate.toMillis()
            dayOffMap[dayOffMs] = 'vendor'
        }
    })

    return (
        <Container
            className={classes.VendorContainer}
            style={{ height: '100%' }}
        >
            {/* Start Spacers */}

            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    boxSizing: 'border-box',
                }}
            >
                <DayBlocks
                    {...props}
                    startGridPos={0}
                    endGridPos={startGridPos - 1}
                    bgColor="transparent"
                    timezoneState={timezoneState}
                />
            </div>

            {/* Work Days */}
            <div
                style={{
                    height: '100%',
                    display: 'flex',
                    boxSizing: 'border-box',
                    position: 'relative',
                }}
            >
                <DragIcon
                    {...props}
                    className={classes.Icon}
                    color={templateService.service.color}
                    size={iconSize}
                    direction="left"
                    ariaLabel="Move start date one day earlier"
                    left={-iconSize - halfArrowGapPx}
                    onClick={handleStartLeftClick}
                />
                <DragIcon
                    {...props}
                    className={classes.Icon}
                    color={templateService.service.color}
                    size={iconSize}
                    direction="right"
                    ariaLabel="Move start date one day later"
                    left={halfArrowGapPx}
                    onClick={handleStartRightClick}
                />
                <DragIcon
                    {...props}
                    className={classes.Icon}
                    color={templateService.service.color}
                    size={iconSize}
                    direction="left"
                    ariaLabel="Move end date one day earlier"
                    right={halfArrowGapPx}
                    onClick={handleEndLeftClick}
                />
                <DragIcon
                    {...props}
                    className={classes.Icon}
                    color={templateService.service.color}
                    size={iconSize}
                    direction="right"
                    ariaLabel="Move end date one day later"
                    right={-iconSize - halfArrowGapPx}
                    onClick={handleEndRightClick}
                />
                <Tooltip title="Set start date" arrow>
                    <button
                        type="button"
                        className={classes.Icon}
                        aria-label="Set start date"
                        style={{
                            height: iconSize,
                            width: iconSize,
                            padding: 0,
                            backgroundColor: mix_hexes(
                                templateService.service.color,
                                '#ffffff',
                            ),
                            zIndex: 1,
                            position: 'absolute',
                            top: `calc(50% - ${iconSize / 2}px)`,
                            left: -iconSize / 2,
                            borderRadius: props.theme.shape.borderRadius,
                            border: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={(e) => setAnchorStartCalendar(e.currentTarget)}
                    >
                        <EventIcon style={{ fontSize: iconSize * 0.7 }} />
                    </button>
                </Tooltip>
                <Tooltip title="Set end date" arrow>
                    <button
                        type="button"
                        className={classes.Icon}
                        aria-label="Set end date"
                        style={{
                            height: iconSize,
                            width: iconSize,
                            padding: 0,
                            backgroundColor: mix_hexes(
                                templateService.service.color,
                                '#ffffff',
                            ),
                            zIndex: 1,
                            position: 'absolute',
                            top: `calc(50% - ${iconSize / 2}px)`,
                            right: -iconSize / 2,
                            borderRadius: props.theme.shape.borderRadius,
                            border: 0,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={(e) => setAnchorEndCalendar(e.currentTarget)}
                    >
                        <EventIcon style={{ fontSize: iconSize * 0.7 }} />
                    </button>
                </Tooltip>

                <Popover
                    open={Boolean(anchorStartCalendar)}
                    anchorEl={anchorStartCalendar}
                    onClose={() => setAnchorStartCalendar(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <div style={{ padding: 8 }}>
                        <KeyboardDatePicker
                            variant="static"
                            value={vendorStartDate.toJSDate()}
                            minDate={projectStartDate.toJSDate()}
                            maxDate={
                                vendorEndDate < projectEndDate
                                    ? vendorEndDate.toJSDate()
                                    : projectEndDate.toJSDate()
                            }
                            onChange={handleStartDatePickerChange}
                            onClose={() => setAnchorStartCalendar(null)}
                            autoOk
                        />
                    </div>
                </Popover>
                <Popover
                    open={Boolean(anchorEndCalendar)}
                    anchorEl={anchorEndCalendar}
                    onClose={() => setAnchorEndCalendar(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <div style={{ padding: 8 }}>
                        <KeyboardDatePicker
                            variant="static"
                            value={vendorEndDate.toJSDate()}
                            minDate={
                                vendorStartDate > projectStartDate
                                    ? vendorStartDate.toJSDate()
                                    : projectStartDate.toJSDate()
                            }
                            maxDate={projectEndDate.toJSDate()}
                            onChange={handleEndDatePickerChange}
                            onClose={() => setAnchorEndCalendar(null)}
                            autoOk
                        />
                    </div>
                </Popover>

                <DayBlocks
                    {...props}
                    startGridPos={startGridPos}
                    endGridPos={endGridPos}
                    bgColor={props.color}
                    dayOffMap={dayOffMap}
                    timezoneState={timezoneState}
                />
            </div>

            {/* End Spacers */}
            <div
                style={{
                    height: '100%',
                    display: 'flex',

                    boxSizing: 'border-box',
                }}
            >
                <DayBlocks
                    {...props}
                    startGridPos={endGridPos + 1}
                    endGridPos={lastGridPos - 1}
                    bgColor="transparent"
                    timezoneState={timezoneState}
                />
            </div>
        </Container>
    )
}

interface DayBlocksProps extends Props {
    startGridPos: number
    endGridPos: number
    bgColor: string
    dayOffMap?: DayOffMap
    timezoneState: TimezoneState
}

const DayBlocks = (props: DayBlocksProps) => {
    const {
        startGridPos,
        endGridPos,
        gridState,
        templateServiceVendor,
        templateService,
        dayOffMap,
        timezoneState,
        workordersByVendorDate,
    } = props

    const dayBlocks: JSX.Element[] = []

    for (
        let curGridPos = startGridPos;
        curGridPos <= endGridPos;
        curGridPos += 1
    ) {
        let leftRad = 0
        let rightRad = 0

        if (curGridPos === startGridPos) {
            leftRad = props.theme.shape.borderRadius
        }

        if (curGridPos === endGridPos) {
            rightRad = props.theme.shape.borderRadius
        }

        const curDate = gridState.convertGridPositionToDate(curGridPos)
        const curDateMs = curDate.toMillis()

        let dayOffMarker: JSX.Element | null = null

        if (dayOffMap) {
            const dayOffVal = dayOffMap[curDateMs]

            if (dayOffVal) {
                dayOffMarker = (
                    <Container
                        style={{
                            backgroundColor: 'rgba(30,30,30,0.2)',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {dayOffVal === 'global' && (
                            <CloseIcon fontSize="large" />
                        )}
                        {dayOffVal === 'vendor' && (
                            <TimerOffIcon fontSize="large" />
                        )}
                    </Container>
                )
            }
        }

        dayBlocks.push(
            <div
                key={`VND-${templateServiceVendor.id}}-SRV-${templateService.id}-DAY-${curDateMs}`}
                style={{
                    cursor: 'pointer',
                    marginBottom: MARGIN,
                    marginTop: MARGIN,
                    width: DAY_CELL_W,
                    backgroundColor: props.bgColor,
                    boxSizing: 'border-box',
                    borderTopLeftRadius: leftRad,
                    borderBottomLeftRadius: leftRad,
                    borderTopRightRadius: rightRad,
                    borderBottomRightRadius: rightRad,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {dayOffMarker}
                <WorkOrderCell
                    templateServiceVendor={templateServiceVendor}
                    workordersByVendorDate={workordersByVendorDate}
                    curDateMs={curDateMs}
                    timezoneState={timezoneState}
                />
            </div>,
        )
    }

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                boxSizing: 'border-box',
            }}
        >
            {dayBlocks}
        </div>
    )
}

type DayOffMap = { [dateMs: number]: 'vendor' | 'global' | 'restricted' }

interface WorkOrderCellProps {
    templateServiceVendor: TemplateServiceVendor
    workordersByVendorDate: Map<string, TemplateWorkorder[]>
    curDateMs: number
    timezoneState: TimezoneState
}

const WorkOrderCell: React.FC<WorkOrderCellProps> = React.memo(
    ({
        templateServiceVendor,
        workordersByVendorDate,
        curDateMs,
        timezoneState,
    }) => {
        const targetDateString = DateTime.fromMillis(curDateMs)
            .setZone(timezoneState.currentTimezone)
            .toISODate()

        if (!targetDateString) {
            return null
        }

        const key = `${templateServiceVendor.id}-${targetDateString}`
        const workordersToUse = workordersByVendorDate.get(key) ?? []

        // Only show icon if there are workorders
        if (workordersToUse.length <= 0) {
            return null
        }

        let hasPriorityUnit = false

        const unitNames: string[] = []
        workordersToUse.forEach((workorder) => {
            // Extract unit name from the workorder data structure
            const unitName = `${workorder.template_unit?.unit?.folder.name} - ${workorder.template_unit?.unit?.name}`
            if (unitName) {
                unitNames.push(unitName)
            }
            if (workorder.is_priority_slice) {
                hasPriorityUnit = true
            }
        })

        const workOrderBox = (
            <div
                style={{
                    backgroundColor: hasPriorityUnit ? 'red' : 'black',
                    color: 'white',
                    borderRadius: 4,
                    padding: '2px 6px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    minWidth: '30px',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {workordersToUse.length}
            </div>
        )

        const tooltipContent = (
            <div>
                <div>
                    <strong>Work Orders: {workordersToUse.length}</strong>
                </div>
                {unitNames.length > 0 && (
                    <div>
                        <div>
                            <strong>Units:</strong>
                        </div>
                        {unitNames.map((unitName, index) => (
                            <div key={index}>• {unitName}</div>
                        ))}
                    </div>
                )}
            </div>
        )

        return (
            <Tooltip title={tooltipContent} arrow>
                {workOrderBox}
            </Tooltip>
        )
    },
)
